'use client';

import 'swiper/css';

import Image from 'next/image';
import { useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { LineArrowLeftIcon } from '@/app/_components/icons/line';
import type { _IStickerCard } from '@/app/(football)/[team]/_components/common/StickerCard/_hooks/useStickerCard';
import type { StickerCategories } from '@/app/(football)/[team]/_context/types';
import { useGetStickerListWithInitQuery } from '@/store/server/common/football/football.query';
import type { OriginalsName } from '@/store/server/common/originals/originals.dto';

import CategoryFilter from './CategoryFilter';
import StickerItem from './StickerItem';

interface StickerListProps {
  originalsName: OriginalsName;
  stickerCard: _IStickerCard | null;
  onAddSticker: (idx: string, src: string) => void;
  onSelectSticker: (idx: string) => void;
  onStepToSelectCard: () => void;
  teamSlug: string;
  stickerCategories: StickerCategories;
}

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

export default function StickerList({
  originalsName,
  stickerCard,
  onAddSticker,
  onSelectSticker,
  onStepToSelectCard,
  teamSlug,
  stickerCategories,
}: StickerListProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('전체');

  const { data: stickerListData, isLoading } = useGetStickerListWithInitQuery({ originalsName });

  const addedStickerIdxSet = useMemo(() => {
    if (!stickerCard?.stickers) return new Set<string>();
    return new Set(stickerCard.stickers.map((s) => s.idx));
  }, [stickerCard]);

  const filteredStickers = useMemo(() => {
    if (!stickerListData) return [];
    if (selectedCategory === '전체') return stickerListData;
    return stickerListData.filter((s) => s.category === selectedCategory);
  }, [stickerListData, selectedCategory]);

  // Chunk stickers into pairs (1 col x 2 rows per slide)
  const stickerPairs = useMemo(() => chunkArray(filteredStickers, 2), [filteredStickers]);

  if (isLoading) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-4 px-5 py-[14px]">
          <div className="flex gap-2">
            {[60, 60, 60].map((w, i) => (
              <div key={i} className="animate-pulse rounded-full bg-white/10" style={{ width: w, height: 32 }} />
            ))}
          </div>
        </div>
        <div className="flex gap-2 overflow-hidden pl-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex w-[56px] shrink-0 flex-col gap-1">
              <div className="flex flex-col items-center gap-1">
                <div className="h-[48px] w-[48px] animate-pulse rounded bg-white/10" />
                <div className="h-[18px] w-[40px] animate-pulse rounded bg-white/10" />
              </div>
              <div className="flex flex-col items-center gap-1">
                <div className="h-[48px] w-[48px] animate-pulse rounded bg-white/10" />
                <div className="h-[18px] w-[40px] animate-pulse rounded bg-white/10" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Category filter row */}
      <div className="flex items-center gap-4 px-5">
        <div className="flex w-full items-center gap-4 py-[14px]">
          <button type="button" onClick={onStepToSelectCard} className="shrink-0 cursor-pointer p-1">
            <LineArrowLeftIcon size={16} color="#ffffff" />
          </button>
          <CategoryFilter categories={stickerCategories} selected={selectedCategory} onSelect={setSelectedCategory} />
        </div>
      </div>

      {/* Sticker swiper */}
      {filteredStickers.length === 0 ? (
        <div className="flex h-[144px] flex-col items-center justify-center gap-2">
          <Image
            src={`https://cdn.kottonseed.io/assets/football/2026/${teamSlug}/icons/icon_illust_card.webp`}
            alt="스티커 없음"
            width={32}
            height={32}
          />
          <p className="typo-body-6 text-white">스티커가 없어요</p>
        </div>
      ) : (
        <Swiper slidesPerView="auto" spaceBetween={8} className="w-full !pl-5">
          {stickerPairs.map((pair, pairIdx) => (
            <SwiperSlide key={pairIdx} style={{ width: 56 }}>
              <div className="flex w-[56px] flex-col gap-2">
                {pair.map((sticker) => (
                  <StickerItem
                    key={sticker.stickerIdx}
                    stickerIdx={sticker.stickerIdx}
                    src={sticker.src}
                    point={sticker.point}
                    useYN={sticker.useYN}
                    originalsName={originalsName}
                    isAdded={addedStickerIdxSet.has(sticker.stickerIdx)}
                    onAddSticker={onAddSticker}
                    onSelectSticker={onSelectSticker}
                    teamSlug={teamSlug}
                    cardIdx={stickerCard?.metadataIdx ?? 0}
                  />
                ))}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  );
}
